/**
 * Sync plast: localStorage cache ⇄ server baza (seja 219, Workstream A4).
 *
 * Načelo: SERVER = vir resnice, localStorage = optimistični cache. Store akcije
 * naredijo takojšen lokalni update + tu sprožijo per-entity API mutacijo
 * (best-effort, fire-and-forget). Če server ni dosegljiv / user ni prijavljen,
 * sync TIHO miruje → aplikacija deluje naprej samo na localStorage (additivno).
 *
 * Push funkcije so no-op dokler `enabled` ni true (postavi ga initSync po uspešnem
 * /api/zadeve 200). Tako ne pošiljamo med SSR/brez-auth.
 *
 * Debounce + flush ob pagehide/visibilitychange (vzorec iz seje 213,
 * reference_canvas_quota_loss) — da zadnji streaming update / urejanje ne uide ob
 * zaprtju zavihka.
 *
 * Krožni uvoz z zadeva-store je varen: obe strani uporabljata uvoze le ZNOTRAJ
 * funkcij (ne ob evalvaciji modula).
 */

import { useZadevaStore } from '@/lib/stores/zadeva-store';
import type { Message } from '@/lib/stores/zadeva-store';

let enabled = false;
const loadedDetail = new Set<string>(); // zadeve, ki imajo naložene messages (lazy)
const MIGRATION_FLAG = 'tribonianus-migrated-to-server';

export function isSyncEnabled(): boolean {
  return enabled;
}

// ---- Debounce z možnostjo takojšnjega flusha ----

const timers = new Map<string, ReturnType<typeof setTimeout>>();
const pending = new Map<string, () => void>();

function schedule(key: string, fn: () => void, ms = 700): void {
  pending.set(key, fn);
  const prev = timers.get(key);
  if (prev) clearTimeout(prev);
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      const f = pending.get(key);
      pending.delete(key);
      f?.();
    }, ms)
  );
}

function flushAll(): void {
  for (const [key, f] of Array.from(pending.entries())) {
    const t = timers.get(key);
    if (t) clearTimeout(t);
    timers.delete(key);
    f();
  }
  pending.clear();
}

async function bestEffort(input: string, init?: RequestInit): Promise<Response | null> {
  try {
    return await fetch(input, { ...init, headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) } });
  } catch {
    return null;
  }
}

// ---- Push (kliče jih zadeva-store; no-op dokler ni enabled) ----

export function remoteCreateZadeva(z: { id: string; title: string; status: string }): void {
  if (!enabled) return;
  loadedDetail.add(z.id); // novo ustvarjena zadeva ima vsebino lokalno → ne lazy-load prek nje
  void bestEffort('/api/zadeve', { method: 'POST', body: JSON.stringify(z) });
}

export function remoteDeleteZadeva(id: string): void {
  if (!enabled) return;
  loadedDetail.delete(id);
  void bestEffort(`/api/zadeve/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function remotePatchZadeva(id: string, patch: Record<string, unknown>): void {
  if (!enabled) return;
  // Združi zaporedne patche iste zadeve (npr. searchContext + summary) v en PUT.
  const key = `patch:${id}`;
  const merged = { ...(patchBuffer.get(id) ?? {}), ...patch };
  patchBuffer.set(id, merged);
  schedule(key, () => {
    const data = patchBuffer.get(id) ?? {};
    patchBuffer.delete(id);
    void bestEffort(`/api/zadeve/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) });
  });
}
const patchBuffer = new Map<string, Record<string, unknown>>();

export function remotePushMessage(zadevaId: string, ordinal: number, message: Message): void {
  if (!enabled) return;
  // Debounce per (zadeva, ordinal) — streaming chunk updati se združijo v ~1 push.
  schedule(`msg:${zadevaId}:${ordinal}`, () => {
    void bestEffort(`/api/zadeve/${encodeURIComponent(zadevaId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ ordinal, message }),
    });
  }, 600);
}

export function remoteDeleteMessage(zadevaId: string, ordinal: number): void {
  if (!enabled) return;
  void bestEffort(`/api/zadeve/${encodeURIComponent(zadevaId)}/messages?ordinal=${ordinal}`, { method: 'DELETE' });
}

export function remoteClearMessages(zadevaId: string): void {
  if (!enabled) return;
  void bestEffort(`/api/zadeve/${encodeURIComponent(zadevaId)}/messages?all=1`, { method: 'DELETE' });
}

export function remotePutCanvas(doc: {
  id: string; format: string; title: string; html: string; metadata?: unknown; turns?: unknown;
}): void {
  if (!enabled) return;
  schedule(`canvas:${doc.id}`, () => {
    void bestEffort(`/api/canvas/${encodeURIComponent(doc.id)}`, { method: 'PUT', body: JSON.stringify(doc) });
  });
}

export function remoteDeleteCanvas(id: string): void {
  if (!enabled) return;
  void bestEffort(`/api/canvas/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---- Hidracija + migracija (kliče SyncProvider) ----

interface ServerZadevaMeta {
  id: string; title: string; status: string; messageCount: number; createdAt: number; updatedAt: number;
}

/** Inicializacija ob loginu: preveri auth, migriraj (enkrat), hidriraj seznam + canvas. */
export async function initSync(): Promise<void> {
  const res = await bestEffort('/api/zadeve', { method: 'GET' });
  if (!res || res.status === 401) {
    enabled = false;
    return; // ni prijave → ostani na localStorage
  }
  if (!res.ok) return;
  enabled = true;

  // Enkratna migracija obstoječih localStorage zadev (idempotentno: lokalni flag + server dedup).
  await maybeMigrate();

  // Hidriraj seznam (metadata) + globalni canvas.
  await hydrateList();
  await hydrateCanvas();

  // Naloži detajl trenutno aktivne zadeve (da je takoj uporabna).
  const activeId = useZadevaStore.getState().activeZadevaId;
  if (activeId) await loadZadevaDetail(activeId);

  // Flush nedokončanih pushov ob zaprtju/skritju zavihka; ob vrnitvi osveži seznam.
  if (typeof window !== 'undefined') {
    const onHide = () => flushAll();
    window.addEventListener('pagehide', onHide);
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushAll();
      else void refreshOnFocus();
    });
  }
}

/**
 * Osvežitev seznama ob vrnitvi fokusa (hidracija teče sicer le ob nalaganju strani,
 * zato odprt zavihek ne pobere zadev, ustvarjenih na drugi napravi).
 *
 * Samo DODAJA in posodablja — `_applyServerZadeveList` je merge, zato zadeva,
 * izbrisana drugje, tu NE izgine. Namerno: brisanje na podlagi odsotnosti v odgovoru
 * bi ob eni zamaknjeni sinhronizaciji pobrisalo zadevo, ki v bazo še ni prispela.
 *
 * Sporočil se ne dotakne (le metadata + canvas), zato ne more povoziti odgovora,
 * ki se streama v skritem zavihku.
 */
const REFRESH_THROTTLE_MS = 30_000;
let lastRefreshAt = 0;

async function refreshOnFocus(): Promise<void> {
  if (!enabled) return;
  const now = Date.now();
  if (now - lastRefreshAt < REFRESH_THROTTLE_MS) return; // alt-tab sem in tja ne dela zahtevkov
  lastRefreshAt = now;
  await hydrateList();
  await hydrateCanvas();
}

async function maybeMigrate(): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(MIGRATION_FLAG) === 'done') return;

  const state = useZadevaStore.getState();
  const zadeve = state.zadeve.map((z) => ({
    id: z.id, title: z.title, status: z.status, createdAt: z.createdAt, updatedAt: z.updatedAt,
    messages: z.messages, dossierCards: z.dossierCards,
    conversationSummary: z.conversationSummary, summarizedUpToIndex: z.summarizedUpToIndex,
    savedFilters: z.savedFilters, searchContext: z.searchContext, latestNomotehniskaReview: z.latestNomotehniskaReview,
  }));
  const canvasDocs = state.canvasDocs.map((c) => ({
    id: c.id, format: c.format, title: c.title, html: c.html, metadata: c.metadata, turns: c.turns,
  }));

  if (zadeve.length === 0 && canvasDocs.length === 0) {
    localStorage.setItem(MIGRATION_FLAG, 'done');
    return;
  }

  const res = await bestEffort('/api/migrate-local', { method: 'POST', body: JSON.stringify({ zadeve, canvasDocs }) });
  if (res && res.ok) {
    localStorage.setItem(MIGRATION_FLAG, 'done');
  }
}

async function hydrateList(): Promise<void> {
  const res = await bestEffort('/api/zadeve', { method: 'GET' });
  if (!res || !res.ok) return;
  const data = await res.json().catch(() => null);
  if (!data?.zadeve) return;
  useZadevaStore.getState()._applyServerZadeveList(data.zadeve as ServerZadevaMeta[]);
}

async function hydrateCanvas(): Promise<void> {
  const res = await bestEffort('/api/canvas', { method: 'GET' });
  if (!res || !res.ok) return;
  const data = await res.json().catch(() => null);
  if (!data?.canvasDocs) return;
  useZadevaStore.getState()._applyServerCanvas(data.canvasDocs);
}

/** Lazy naloži polno vsebino zadeve (messages/docs/dossier) ob prvem odprtju. */
export async function loadZadevaDetail(id: string): Promise<void> {
  if (!enabled || loadedDetail.has(id)) return;
  loadedDetail.add(id); // optimistično (prepreči dvojni fetch); ob napaki odstrani
  const res = await bestEffort(`/api/zadeve/${encodeURIComponent(id)}`, { method: 'GET' });
  if (!res || !res.ok) {
    loadedDetail.delete(id);
    return;
  }
  const data = await res.json().catch(() => null);
  if (!data?.zadeva) {
    loadedDetail.delete(id);
    return;
  }
  useZadevaStore.getState()._applyServerZadevaDetail(id, data.zadeva);
}
